import React, { useState } from "react";
import ButtonComponent from "./ButtonComponent";
import { useAppDispatch, useAppSelector } from "../redux/app/hooks";
import { nanoid } from "nanoid";
import ModalComponent from "./ModalComponent";
import { getAllCart, removeCartData } from "../redux/features/leadUserCart/leadUserCart";
import { toastComponent } from "./ToastComponent";
import JewelMakerLogo from "../assets/Logo/brand-logo.svg";
import { Button } from "./ui";
import { Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

type Props = {
  list: LeadUserCartDesign[];
  name: string;
  totalQunty: number;
  totalwight: number;
};

const TableComponent = ({ list, name }: Props) => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [id, setId] = useState<number>(0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: catalogueId } = useParams();
  let dynamicAttributes: number = 0;
  let isDynamicAttributesEmpty: boolean = false;

  const handleDelete = (e: number) => {
    setId(e);
    setDeleteModal((prev: boolean) => !prev);
  };

  const confirmDelete = (e: number) => {
    dispatch(
      removeCartData({
        cart_id: e,
      })
    ).then((res) => {
      toastComponent(res.payload.message, "success");
      dispatch(getAllCart());
    });
    setDeleteModal((prev: boolean) => !prev);
  };

  // Function to handle navigation to item details
  const handleItemClick = (designId: number, karat: number, dynamicAttributes: any, id: number) => {
    isDynamicAttributesEmpty = dynamicAttributes && dynamicAttributes.length === 0 ? true : false;
    navigate(`/catalogues/${catalogueId}/${designId}/${karat}`, {
      state: { fromCart: true, dynamicAttributes: id, isDynamicAttributesEmpty },
    });
  };

  return (
    <>
      {deleteModal && (
        <ModalComponent setShow={setDeleteModal} show={deleteModal}>
          <div className="flex flex-col h-full">
            <div className="text-primary font-bold px-6 py-4 border-b flex flex-row justify-between">
              Remove From the Cart?
              <button className="text-lg font-bold cursor-pointer hover:text-gray-600 transition-colors" onClick={() => setDeleteModal(false)}>
                ✕
              </button>
            </div>
            <div className="min-h-[3rem] px-6 py-4">
              <p className="text-gray-600">Are you sure you want to remove this item from your cart?</p>
              <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
            </div>
            <div className="flex flex-row justify-between px-6 gap-4 py-4">
              <ButtonComponent variant="primary" CTA="Cancel" onClick={() => setDeleteModal(false)} style={{ flex: 1 }} />
              <ButtonComponent variant="danger" CTA="Remove" onClick={() => confirmDelete(id)} 
                style={{ flex: 1, backgroundColor: "rgb(220, 38, 38)" }}
                />
            </div>
          </div>
        </ModalComponent>
      )}

      <div className="divide-y">
        {/* Table Header - Desktop */}
        <div className="hidden md:grid grid-cols-12 px-6 py-2 text-xs uppercase text-muted-foreground bg-gray-50 font-bold">
          <div className="col-span-3">Item</div>
          <div className="col-span-2">Design No.</div>
          <div className="col-span-1 text-right">Karat</div>
          <div className="col-span-2 text-center">Pieces</div>
          <div className="col-span-1 text-right">Gross WT</div>
          <div className="col-span-1 text-right">Net WT</div>
          <div className="col-span-1 text-right">Total Gross</div>
          <div className="col-span-1 text-right">Total Net</div>
          <div className="col-span-1 text-center">Action</div>
        </div>

        {/* Table Rows */}
        {list.map((item) => {
          const grossWeight = item.CartDesignInfo?.design_weights?.[0]?.gross_weight || 0;
          const netWeight = item.CartDesignInfo?.design_weights?.[0]?.net_weight || 0;
          const totalGrossWeight = item.CartDesignInfo?.design_weights?.[0]?.design_wise_total_gross_weight || 0;
          const totalNetWeight = item.CartDesignInfo?.design_weights?.[0]?.design_wise_total_net_weight || 0;
          const quantity = item.quantity || 1;
          const designNo = item.CartDesignInfo?.variant_number || "N/A";
          const karat = item.karat || "N/A";
          const imageUrl = item?.CartDesignInfo?.image_files?.[0]?.image_file || JewelMakerLogo;

          return (
            <div key={nanoid()} className="grid grid-cols-12 gap-4 items-center px-4 md:px-6 py-4">
              {/* Image + Title */}
              <div className="col-span-12 md:col-span-3 flex items-center gap-4 cursor-pointer group" onClick={() => handleItemClick(item.design_id, item.karat, item.DynamicAttributes, item.id)}>
                <img
                  src={imageUrl}
                  alt={designNo}
                  className="h-16 w-16 rounded-xl object-cover ring-1 ring-border group-hover:ring-2 group-hover:ring-dynamicColor-primary transition-all"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = "https://jm-manufacturer.s3.ap-south-1.amazonaws.com/jm-manufacturer/siteSetting/image/JM%20logo.jpg";
                    target.className = "h-16 w-16 rounded-xl object-contain p-2 ring-1 ring-border group-hover:ring-2 group-hover:ring-[#396897] transition-all";
                  }}
                />
                <div>
                  <div className="font-medium group-hover:text-dynamicColor-primary transition-colors">{name}</div>
                </div>
              </div>

              <div className="col-span-6 md:col-span-2 text-sm">{designNo}</div>
              <div className="col-span-3 md:col-span-1 text-right text-sm">{karat}K</div>

              {/* Pieces Display Only */}
              <div className="col-span-6 md:col-span-2 flex items-center justify-center">
                <div className="text-sm font-medium text-gray-900">{quantity}</div>
              </div>

              <div className="col-span-3 md:col-span-1 text-right text-sm">{parseFloat(String(grossWeight)).toFixed(3)}</div>
              <div className="col-span-3 md:col-span-1 text-right text-sm">{parseFloat(String(netWeight)).toFixed(3)}</div>

              {/* Total Net */}
              <div className="col-span-3 md:col-span-1 text-right font-medium text-sm">{parseFloat(String(totalGrossWeight)).toFixed(3)}</div>
              <div className="col-span-3 md:col-span-1 text-right font-medium text-sm">{parseFloat(String(totalNetWeight)).toFixed(3)}</div>

              {/* Actions */}
              <div className="col-span-3 md:col-span-1 flex items-center justify-center">
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="h-8 w-8">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Remark */}
              <div className="col-span-12 pt-2">
                <div className="text-sm font-semibold text-gray-700 mb-2">Remarks</div>
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg border min-h-[60px]">{item.remark || "No remarks added"}</div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default TableComponent;
